import { Component, OnInit } from '@angular/core';
import { PwaService } from '@app/core/services/pwa.service';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-install-prompt',
  templateUrl: './install-prompt.component.html',
  styleUrls: ['./install-prompt.component.scss']
})
export class InstallPromptComponent implements OnInit {
  showPrompt = false;
  isIos = false;

  private readonly STORAGE_KEY = 'pwa_prompt_last_dismissed_time';
  private readonly COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 Hours

  constructor(private pwaService: PwaService) {}

  ngOnInit(): void {
    if (!environment.enablePwa) {
        this.showPrompt = false;
        return;
    }
    
    this.isIos = this.pwaService.isIos();

    // Check if running on a TV browser (WebOS, Tizen, etc.)
    // PWAs don't work the same way on TVs, so skip the prompt
    if (this.isTvBrowser()) {
        this.showPrompt = false;
        console.log('PWA Prompt: Skipped on TV browser');
        return;
    }

    // Check if already installed (standalone mode)
    if (this.pwaService.isInStandaloneMode()) {
        this.showPrompt = false;
        return;
    }

    // Android/Desktop: Listen to installable event
    this.pwaService.isInstallable$.subscribe((canInstall) => {
      if (canInstall) {
        this.checkAndShowPrompt();
      }
    });

    // iOS: Show tip if on iOS and not standalone
    if (this.isIos && !this.pwaService.isInStandaloneMode()) {
        // iOS doesn't have 'beforeinstallprompt', so we check manually
        // Delay slightly to not clutter start
        setTimeout(() => {
            this.checkAndShowPrompt();
        }, 3000);
    }
  }

  private checkAndShowPrompt() {
      if (!environment.enablePwa) {
          this.showPrompt = false;
          return;
      }

      // Clean up legacy key if exists
      localStorage.removeItem('pwa_prompt_dismissed');

      const lastDismissed = localStorage.getItem(this.STORAGE_KEY);
      if (lastDismissed) {
          const elapsed = Date.now() - parseInt(lastDismissed, 10);
          if (elapsed < this.COOLDOWN_MS) {
              console.log('PWA Prompt: Cooldown active. Skipping.');
              this.showPrompt = false;
              return;
          }
      }
      
      this.showPrompt = true;
  }

  install() {
    this.pwaService.installPwa();
    this.showPrompt = false;
  }

  dismiss() {
    this.showPrompt = false;
    // Remember dismissal timestamp
    localStorage.setItem(this.STORAGE_KEY, Date.now().toString());
  }

  /**
   * Detect if running on a TV browser
   * Smart TVs (WebOS, Tizen) don't support PWA installation
   */
  private isTvBrowser(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    return /WebOS|Tizen|BRAVIA|NetCast|SMART-TV|SmartTV|Web0S/i.test(ua);
  }
}
